import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Support as SupportIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import API from '../config';
import { AuthContext } from '../../contexts/AuthContext';
import SupportTicketModal from './SupportTicketModal';

export default function Support() {
  const theme = useTheme();
  const { authData } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (authData) {
      fetchTickets();
    }
  }, [authData]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/api/support/tickets`);
      setTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (subject, message, category) => {
    setSubmitting(true);
    try {
      await api.post(`${API}/api/support/tickets`, {
        subject,
        message,
        category,
      });
      toast.success('Support ticket submitted successfully!');
      setModalOpen(false);
      fetchTickets();
    } catch (err) {
      console.error('Error submitting ticket:', err);
      toast.error(err.response?.data?.error || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'followed_up':
        return 'info';
      case 'dismissed':
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircleIcon />;
      case 'followed_up':
        return <ScheduleIcon />;
      case 'dismissed':
        return <CancelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'resolved':
        return 'Resolved';
      case 'followed_up':
        return 'Followed Up';
      case 'dismissed':
        return 'Dismissed';
      default:
        return 'Pending';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'billing':
        return 'Billing';
      case 'technical':
        return 'Technical';
      case 'account':
        return 'Account';
      default:
        return 'Other';
    }
  };

  if (!authData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Please log in to access support.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 4, maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            Support
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            New Ticket
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Submit a support ticket or view your existing tickets
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tickets.length === 0 ? (
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: 2,
          }}>
            <SupportIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No support tickets yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Need help? Submit a ticket and we'll get back to you as soon as possible.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Create Your First Ticket
            </Button>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Grid container spacing={3}>
            {tickets.map((ticket) => (
              <Grid item xs={12} key={ticket._id}>
                <Card sx={{ 
                  borderRadius: 3,
                  boxShadow: 2,
                  transition: 'all 0.3s ease',
                  width: '100%',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {ticket.subject}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          label={getCategoryLabel(ticket.category)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip 
                          icon={getStatusIcon(ticket.status)}
                          label={getStatusLabel(ticket.status)}
                          color={getStatusColor(ticket.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 3,
                        whiteSpace: 'pre-line',
                        lineHeight: 1.7,
                      }}
                    >
                      {ticket.message}
                    </Typography>
                    
                    <Box sx={{ 
                      pt: 2.5,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Created: {ticket.createdAt 
                          ? new Date(ticket.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'N/A'}
                      </Typography>
                      {ticket.resolution_note && (
                        <Box sx={{ 
                          p: 2.5, 
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          width: '100%',
                        }}>
                          <Typography variant="caption" fontWeight={600} color="info.main" sx={{ display: 'block', mb: 1 }}>
                            Response:
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
                            {ticket.resolution_note}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <SupportTicketModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitTicket}
        loading={submitting}
      />
    </Container>
  );
}

